import type { Nullable } from "core/types";
import { RegisterClass } from "core/Misc/typeStore";
import { Vector3 } from "core/Maths/math.vector";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

/**
 * Operations supported by the Trigonometry block
 */
export enum RewardTrigBlockOperations {
    /** Cos */
    Cos,
    /** Sin */
    Sin,
    /** Abs */
    Abs,
    /** Exp */
    Exp,
    /** Round */
    Round,
    /** Floor */
    Floor,
    /** Ceiling */
    Ceiling,
    /** Square root */
    Sqrt,
    /** Log */
    Log,
    /** Tangent */
    Tan,
    /** Arc tangent */
    ArcTan,
    /** Arc cosinus */
    ArcCos,
    /** Arc sinus */
    ArcSin,
    /** Sign */
    Sign,
    /** Negate */
    Negate,
    /** OneMinus */
    OneMinus,
    /** Reciprocal */
    Reciprocal,
    /** ToDegrees */
    ToDegrees,
    /** ToRadians */
    ToRadians,
    /** Fract */
    Fract,
    /** Exp2 */
    Exp2,
    Relu,
    Sigmoid,
    Softplus,
    Swish,
    Tanh,
    Selu
}

function OperationString(operation : RewardTrigBlockOperations) : string {
    if( typeof operation === 'number' )
        return RewardTrigBlockOperations[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardTrigBlockOperations as any)[op];
}

/**
 * Block used to apply trigonometry operation to floats
 */
export class RewardTrigBlock extends RewardGraphNode {
    /**
     * Gets or sets the operation applied by the block
     */
    @editableInPropertyPage("Operation", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Cos", value: RewardTrigBlockOperations.Cos },
            { label: "Sin", value: RewardTrigBlockOperations.Sin },
            { label: "Abs", value: RewardTrigBlockOperations.Abs },
            { label: "Exp", value: RewardTrigBlockOperations.Exp },
            { label: "Exp2", value: RewardTrigBlockOperations.Exp2 },
            { label: "Round", value: RewardTrigBlockOperations.Round },
            { label: "Floor", value: RewardTrigBlockOperations.Floor },
            { label: "Ceiling", value: RewardTrigBlockOperations.Ceiling },
            { label: "Sqrt", value: RewardTrigBlockOperations.Sqrt },
            { label: "Log", value: RewardTrigBlockOperations.Log },
            { label: "Tan", value: RewardTrigBlockOperations.Tan },
            { label: "ArcTan", value: RewardTrigBlockOperations.ArcTan },
            { label: "ArcCos", value: RewardTrigBlockOperations.ArcCos },
            { label: "ArcSin", value: RewardTrigBlockOperations.ArcSin },
            { label: "Sign", value: RewardTrigBlockOperations.Sign },
            { label: "Negate", value: RewardTrigBlockOperations.Negate },
            { label: "OneMinus", value: RewardTrigBlockOperations.OneMinus },
            { label: "Reciprocal", value: RewardTrigBlockOperations.Reciprocal },
            { label: "ToDegrees", value: RewardTrigBlockOperations.ToDegrees },
            { label: "ToRadians", value: RewardTrigBlockOperations.ToRadians },
            { label: "Fract", value: RewardTrigBlockOperations.Fract },
            { label: "Relu", value: RewardTrigBlockOperations.Relu },
            { label: "Sigmoid", value: RewardTrigBlockOperations.Sigmoid },
            { label: "Softplus", value: RewardTrigBlockOperations.Softplus },
            { label: "Swish", value: RewardTrigBlockOperations.Swish },
            { label: "Tanh", value: RewardTrigBlockOperations.Tanh },
            { label: "Selu", value: RewardTrigBlockOperations.Selu },
        ],
    })
    public operation = RewardTrigBlockOperations.Cos;

    /**
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerInput("input", RewardGraphConnectionPointTypes.AutoDetect);
        this.registerOutput("output", RewardGraphConnectionPointTypes.BasedOnInput);

        this._outputs[0]._typeConnectionSource = this._inputs[0];
        this._inputs[0].excludedConnectionPointTypes.push(RewardGraphConnectionPointTypes.Quat);
        this._inputs[0].excludedConnectionPointTypes.push(RewardGraphConnectionPointTypes.PartID);
        this._inputs[0].excludedConnectionPointTypes.push(RewardGraphConnectionPointTypes.Boolean);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardTrigBlock";
    }

    /**
     * Gets the input component
     */
    public get input(): RewardGraphConnectionPoint {
        return this._inputs[0];
    }

    /**
     * Gets the output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock(state: RewardGraphBuildState) {
        super._buildBlock(state);
        let func: Nullable<(value: number) => number> = null;

        switch (this.operation) {
            case RewardTrigBlockOperations.Cos: {
                func = (value: number) => Math.cos(value);
                break;
            }
            case RewardTrigBlockOperations.Sin: {
                func = (value: number) => Math.sin(value);
                break;
            }
            case RewardTrigBlockOperations.Abs: {
                func = (value: number) => Math.abs(value);
                break;
            }
            case RewardTrigBlockOperations.Exp: {
                func = (value: number) => Math.exp(value);
                break;
            }
            case RewardTrigBlockOperations.Exp2: {
                func = (value: number) => Math.pow(2, value);
                break;
            }
            case RewardTrigBlockOperations.Round: {
                func = (value: number) => Math.round(value);
                break;
            }
            case RewardTrigBlockOperations.Floor: {
                func = (value: number) => Math.floor(value);
                break;
            }
            case RewardTrigBlockOperations.Ceiling: {
                func = (value: number) => Math.ceil(value);
                break;
            }
            case RewardTrigBlockOperations.Sqrt: {
                func = (value: number) => Math.sqrt(value);
                break;
            }
            case RewardTrigBlockOperations.Log: {
                func = (value: number) => Math.log(value);
                break;
            }
            case RewardTrigBlockOperations.Tan: {
                func = (value: number) => Math.tan(value);
                break;
            }
            case RewardTrigBlockOperations.ArcTan: {
                func = (value: number) => Math.atan(value);
                break;
            }
            case RewardTrigBlockOperations.ArcCos: {
                func = (value: number) => Math.acos(value);
                break;
            }
            case RewardTrigBlockOperations.ArcSin: {
                func = (value: number) => Math.asin(value);
                break;
            }
            case RewardTrigBlockOperations.Sign: {
                func = (value: number) => Math.sign(value);
                break;
            }
            case RewardTrigBlockOperations.Negate: {
                func = (value: number) => -value;
                break;
            }
            case RewardTrigBlockOperations.OneMinus: {
                func = (value: number) => 1 - value;
                break;
            }
            case RewardTrigBlockOperations.Reciprocal: {
                func = (value: number) => 1 / value;
                break;
            }
            case RewardTrigBlockOperations.ToRadians: {
                func = (value: number) => (value * Math.PI) / 180;
                break;
            }
            case RewardTrigBlockOperations.ToDegrees: {
                func = (value: number) => (value * 180) / Math.PI;
                break;
            }
            case RewardTrigBlockOperations.Fract: {
                func = (value: number) => {
                    if (value >= 0) {
                        return value - Math.floor(value);
                    } else {
                        return value - Math.ceil(value);
                    }
                };
                break;
            }
            case RewardTrigBlockOperations.Relu: {
                func = (value: number) => {
                    if( value > 0 )
                        return value;
                    return 0;
                }
                break;
            }
            case RewardTrigBlockOperations.Sigmoid: {
                func = (value: number) => {
                    return 1. / ( 1. + Math.exp(-value) );
                }
                break;
            }
            case RewardTrigBlockOperations.Softplus: {
                func = (value: number) => {
                    return Math.log( 1. + Math.exp(value) );
                }
                break;
            }
            case RewardTrigBlockOperations.Swish: {
                func = (value: number) => {
                    return value / ( 1. + Math.exp(-value) );
                }
                break;
            }
            case RewardTrigBlockOperations.Tanh: {
                func = (value: number) => {
                    if( Math.abs(value) > 5.0e2 )
                        return Math.sign(value);
                    const ex = Math.exp(value);
                    const enx = Math.exp(-value);
                    return (ex - enx) / (ex + enx);
                }
                break;
            }
            case RewardTrigBlockOperations.Selu: {
                const alpha = 1.6732632423543772848170429916717;
                const scale = 1.0507009873554804934193349852946;
                func = (value: number) => {
                    if( value > 0 )
                        return scale * value;
                    return scale * alpha * Math.expm1(value);
                }
                break;
            }
        }
        if (!func) {
            this.output._storedFunction = null;
            this.output._storedValue = null;
            return;
        }

        switch (this.input.type) {
            case RewardGraphConnectionPointTypes.Float: {
                this.output._storedFunction = (state) => {
                    const source = this.input.getConnectedValue(state);
                    return func!(source);
                };
                break;
            }
            case RewardGraphConnectionPointTypes.Vector3: {
                this.output._storedFunction = (state) => {
                    const source = this.input.getConnectedValue(state);
                    return new Vector3(func!(source.x), func!(source.y), func!(source.z));
                };
                break;
            }
        }

        return this;
    }

    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.operation = OperationString(this.operation);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.operation = StringOperation(serializationObject.operation);
    }

    protected override _dumpPropertiesCode() {
        const codeString =
            super._dumpPropertiesCode() +
            `${this._codeVariableName}.operation = MELS.RewardTrigBlock.${RewardTrigBlockOperations[this.operation]};\n`;
        return codeString;
    }
}

RegisterClass("MELS.RewardTrigBlock", RewardTrigBlock);
